import { put, takeLatest, cancel, cancelled, call, select, fork } from 'redux-saga/effects';
import { navigate } from 'redux-saga-first-router';

import { halt } from '../../sagas';
import { watchSubmitTrimbleLogin } from '../Trimble/trimbleSagas';
import { watchSubmitSocialLogin } from '../SocialAccounts/socialAccountsSagas';
import { watchResendActivation } from '../Profile/profileSagas';
import {
  watchSubmitLogin as watchSubmitCorporateLogin,
  validateCorporateMail,
  getLoginMode,
} from '../CorporateLogin/corporateSagas';
import { errorMessage, linkMessage } from '../CorporateLogin/constants';
import { getCorporateLoginURL } from '../CorporateLogin/utils';
import { Api } from '../Utils/api';
import { loginRedirect, logoutRedirect, getQueryParamByName, extractUserAgentFromReturnTo } from '../Utils/helpers';
import { isLength, isEmail, validateData, isEmailOrUsername } from '../Utils/Validation';
import { warningRules as registerWarnings } from '../Register/registerSagas';
import { CaptchaTypes } from '../../constants';
import { pushAuthEvent } from '../Utils/Analytics/dataLayer';

// Action type mapping for trial and regular login
const ACTION_TYPES = {
  GENERATE_CG_CAPTCHA: {
    trial: 'GENERATE_TRIAL_LOGIN_CG_CAPTCHA',
    regular: 'GENERATE_LOGIN_CG_CAPTCHA',
  },
  SET_ERROR: {
    trial: 'TRIAL_ERROR',
    regular: 'SUBMIT_LOGIN#ERROR',
  },
  SUBMIT_LOGIN_COMPLETE: {
    trial: 'TRIAL_SUBMIT_LOGIN#COMPLETE',
    regular: 'SUBMIT_LOGIN#COMPLETE',
  },
  LOGIN_REQUEST_TOTP: {
    trial: 'TRIAL_LOGIN_REQUEST_TOTP',
    regular: 'REQUEST_TOTP',
  },
  LOADING: {
    trial: 'SET_TRIAL_LOADING',
    regular: 'SET_LOADING',
  },
};

function getActionType(type, isTrial) {
  return isTrial ? ACTION_TYPES[type].trial : ACTION_TYPES[type].regular;
}

export function* watchValidateCaptcha() {
  yield takeLatest('SUBMIT_LOGIN#VALIDATE_RECAPTCHA', handleValidateReCaptcha);
}

export function* watchSubmitTOTP() {
  yield takeLatest('SUBMIT_TOTP', submitTOTP);
}

function* handleValidateReCaptcha({ data }) {
  return yield call(submitLogin, { isTrial: data?.isTrial, captchaAnswer: data?.value });
}

export function* watchGenerateLoginCGCaptcha() {
  yield takeLatest('GENERATE_CG_LOGIN_CAPTCHA', generateLoginCgCaptcha);
}

export function* generateLoginCgCaptcha({ isTrial }) {
  const action = getActionType('GENERATE_CG_CAPTCHA', isTrial);
  const errorAction = getActionType('SET_ERROR', isTrial);

  try {
    yield put({ type: `${action}#START` });
    const data = yield call(() => Api.fetch('/captcha/generate', { method: 'GET', on4xx: 'throw', on5xx: 'throw' }));
    yield put({ type: `${action}#COMPLETE`, data });
  } catch {
    yield put({
      type: errorAction,
      fieldErrors: { captcha: 'Failed to generate CAPTCHA. Please reload the page and try again.' },
    });
  }
}

function* triggerLoginPrerequisites({ reCaptcha, isTrial }) {
  const errorAction = getActionType('SET_ERROR', isTrial);
  try {
    const loginData = yield select((state) => (isTrial ? state.trial.fields : state.login));
    const { loginCaptchaEnabled, captcha_type } = yield getLoginMode(loginData.email);

    if (!loginCaptchaEnabled) {
      yield call(submitLogin, { isTrial });
      return;
    }

    // proceed to login with CG Captcha
    if (captcha_type === CaptchaTypes.CG_CAPTCHA) {
      yield call(proceedToLoginWithCGCaptcha, isTrial, errorAction);
      return;
    }

    // proceed to login with reCaptcha
    yield call(proceedToLoginWithReCaptcha, reCaptcha, errorAction);
  } catch (err) {
    yield put({ type: errorAction, error: { message: 'Failed to proceed with login.' } });
  }
}

function* proceedToLoginWithCGCaptcha(isTrial, errorAction) {
  const cgCaptchaGUID = yield select((state) =>
    isTrial ? state.trial.loginCaptcha.cgcaptcha.id : state.login.cgcaptcha.id,
  );
  const cgCaptchaValue = yield select((state) =>
    isTrial ? state.trial.fields.cgCaptchaValue : state.login.cgCaptchaValue,
  );

  if (!cgCaptchaValue) {
    yield put({ type: errorAction, error: { message: 'Please complete the CAPTCHA challenge.' } });
    return;
  }
  yield call(submitLogin, { isTrial, captchaAnswer: cgCaptchaValue, captchaGUID: cgCaptchaGUID });
}

function* proceedToLoginWithReCaptcha(reCaptcha, errorAction) {
  yield call(() => reCaptcha.reset());
  const recaptchaExecution = yield call(() => reCaptcha.execute({ action: 'login' }));

  if (!recaptchaExecution) {
    yield put({ type: errorAction, error: { message: 'Failed to execute Captcha validation.' } });
  }
}

function* submitLogin({ isTrial = false, captchaAnswer, captchaGUID }) {
  const fieldErrorAction = getActionType('SET_ERROR', isTrial);
  try {
    const loginData = yield select((state) => (isTrial ? state.trial.fields : state.login));
    const fieldErrors = yield validateData(loginData, loginValidations);
    if (fieldErrors) {
      yield put({ type: fieldErrorAction, fieldErrors });
      yield cancel();
    }

    const trialParams = {};
    if (isTrial) {
      trialParams.isTrial = true;
    }

    const userAgent = extractUserAgentFromReturnTo();
    const { RequiredPasswordUpdate, RequiredProfileUpdate, TOTPRequired, TOTPSessionChallenge, ...otherData } =
      yield call(Api.fetch, '/account/login', {
        method: 'POST',
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password,
          captchaAnswer,
          userAgent,
          captchaGUID,
          ...trialParams,
        }),
        onUnauthorized: 'throw',
        on5xx: 'throw',
      });

    yield put({
      type: getActionType('SUBMIT_LOGIN_COMPLETE', isTrial),
      data: { RequiredPasswordUpdate, RequiredProfileUpdate, ...otherData },
    });

    yield call(pushAuthEvent, {
      action: 'login',
      authMethod: 'SSO',
      guid: otherData.UserGUID,
      email: loginData.email,
    });

    // If TOTP (2FA) is required, show the TOTP form screen
    if (TOTPRequired) {
      yield put({
        type: getActionType('LOGIN_REQUEST_TOTP', isTrial),
        requestTOTP: true,
        challenge: TOTPSessionChallenge,
      });
      return;
    }

    yield call(redirectAfterSuccessfulLogin, isTrial);
  } catch (error) {
    if (error.message.indexOf('Incorrect email or password.') > -1) {
      yield put({ type: fieldErrorAction, fieldErrors: { password: error.message } });
    } else if (error.message.indexOf('Incorrect answer to captcha') > -1) {
      yield put({ type: fieldErrorAction, fieldErrors: { captcha: error.message } });
    } else {
      yield put({ type: fieldErrorAction, fieldErrors: { password: error.message }, error });
    }
  }
}

function* redirectAfterSuccessfulLogin(isTrial) {
  const { RequiredPasswordUpdate, RequiredProfileUpdate } = yield select((state) =>
    isTrial ? state.trial : state.login,
  );
  const returnToAddr = getQueryParamByName('return_to');

  if (RequiredPasswordUpdate) {
    const returnTo = isTrial ? `${window.location.origin}/trial/success` : returnToAddr || window.HOME_PAGE;
    window.location = `/account/password-change?return_to=${encodeURIComponent(
      returnTo,
    )}&required_password_update=true`;
    yield cancel();
    // ToDo: bring it back after domain switch
    // yield put(
    //   navigate('PASSWORD_CHANGE', null, {
    //     query: {
    //       return_to: returnTo,
    //       required_password_update: true,
    //     },
    //   }),
    // );
  }
  if (isTrial) {
    const redirectAddress = new URL(returnToAddr || window.TRIAL_ADDR);
    redirectAddress.searchParams.append('isNewAccount', false);
    window.location = redirectAddress;
    return;
  }

  if (RequiredProfileUpdate) {
    const returnTo = returnToAddr || window.HOME_PAGE;
    window.location = `/profile/edit?return_to=${encodeURIComponent(returnTo)}`;
    return;
  }

  yield call(loginRedirect, null, getQueryParamByName('return_to'));
}

export function* navigateLogin() {
  try {
    const shouldReauthenticate = getQueryParamByName('prompt') === 'login';
    const { uid } = yield select((state) => state.app);

    yield fork(watchLoginInputChange);
    yield fork(watchResendActivation);
    yield fork(watchValidateCaptcha);
    yield fork(watchSubmitTOTP);
    yield fork(watchGenerateLoginCGCaptcha);
    yield fork(watchSubmitLoginEmail);
    yield fork(watchSubmitCorporateLogin);
    yield fork(watchRequestRegularLogin);
    const prefillEmail = getQueryParamByName('email');
    if (prefillEmail) {
      yield put({ type: 'PREFILL_EMAIL', email: prefillEmail });
    }
    yield put({ type: 'LOGIN_RESET' });
    yield put({ type: 'RESEND_ACTIVATION_CODE_RESET' });

    // Watch for submitLogin if not logged in or legacy login response requested, as
    // user won't be redirected if legacy login flow in use, even if logged in.
    if (!uid || shouldReauthenticate) {
      yield fork(watchSubmitLogin);
      yield fork(watchSubmitTrimbleLogin);
      yield fork(watchSubmitSocialLogin);
      yield call(halt);
    } else {
      const returnTo = getQueryParamByName('return_to');
      if (returnTo) {
        yield call(loginRedirect, null, returnTo);
      } else {
        yield put(navigate('ACCOUNT_EDIT'));
      }
    }
  } finally {
    if (yield cancelled()) {
      const state = yield select((s) => s);
      const routingID = state.routing.id;
      const loginEmail = state.login.email;

      if (routingID === 'PASSWORD_RECOVER' && isEmail(loginEmail)) {
        yield put({ type: 'RECOVER_PASSWORD_INPUT_CHANGE', data: { email: loginEmail } });
      }

      yield put({ type: 'LOGIN_RESET' });
    }
  }
}

export function* watchLoginInputChange() {
  yield takeLatest('LOGIN_INPUT_CHANGE', loginInputChange);
}

export function* loginInputChange() {
  const loginData = yield select((state) => state.login);
  const fieldWarnings = yield call(() => validateData(loginData, warningRules));

  yield put({ type: 'LOGIN_INPUT_CHANGE#WARN', fieldWarnings });
}

export function* navigateLogout() {
  const form = new FormData();
  const userAgent = getQueryParamByName('ua');
  if (userAgent) {
    form.append('ua', userAgent);
  }

  yield fetch(`${window.SSO_GATEWAY}/account/logout`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  yield call(logoutRedirect);
}

export function* watchSubmitLogin() {
  yield takeLatest('SUBMIT_LOGIN', triggerLoginPrerequisites);
}

function* watchRequestRegularLogin() {
  yield takeLatest('REQUEST_REGULAR_LOGIN', requestRegularLogin);
}

function* requestRegularLogin() {
  yield put({ type: 'ALLOW_CORPORATE_LOGIN', allowCorporateLogin: false });
  yield put({ type: 'REQUEST_PASSWORD', requestPassword: true });
}

export function* watchSubmitLoginEmail() {
  yield takeLatest('SUBMIT_LOGIN_EMAIL', submitLoginEmail);
}

function* submitLoginEmail() {
  const email = yield select((state) => state.login.email);
  const fieldErrors = yield validateData(
    { email },
    {
      email: [
        { f: isLength, message: 'Please enter your username or email address.' },
        { f: isEmailOrUsername, message: 'Please enter a valid email address or a username.' },
      ],
    },
  );
  if (fieldErrors) {
    yield put({ type: 'SUBMIT_LOGIN#ERROR', fieldErrors });
    yield cancel();
  }

  const { oidc, password, loginCaptchaEnabled, captcha_type } = yield getLoginMode(email);
  const canLoginWithPassword = password && password.has_password;
  yield put({ type: 'SET_LOGIN_CAPTCHA_TYPE', captchaType: captcha_type });

  // If the user has provided an username (=== not a valid email)
  // proceed with password login and skip corporate SSO flow
  if (!isEmail(email)) {
    yield put({ type: 'ALLOW_CORPORATE_LOGIN', allowCorporateLogin: false });
    yield put({ type: 'REQUEST_PASSWORD', requestPassword: true });
    return;
  }

  if (loginCaptchaEnabled && captcha_type === CaptchaTypes.CG_CAPTCHA) {
    yield fork(generateLoginCgCaptcha, { isTrial: false });
  }

  if (oidc && !canLoginWithPassword) {
    yield put({ type: 'CORPORATE_LOGIN_INPUT_CHANGE', data: { id: 'email', value: email } });
    yield put({ type: 'SUBMIT_CORPORATE_LOGIN' });
    return;
  }

  if (oidc && canLoginWithPassword) {
    yield put({ type: 'CORPORATE_LOGIN_INPUT_CHANGE', data: { id: 'email', value: email } });
    yield put({ type: 'ALLOW_CORPORATE_LOGIN', allowCorporateLogin: true });
    yield put({ type: 'REQUEST_PASSWORD', requestPassword: false });
    return;
  }

  yield put({ type: 'ALLOW_CORPORATE_LOGIN', allowCorporateLogin: false });
  yield put({ type: 'REQUEST_PASSWORD', requestPassword: true });
}

// TOTP verification flow
export function* submitTOTP({ totpCode, rememberDevice = false, isTrial = false }) {
  try {
    yield put({ type: getActionType('LOADING', isTrial), loading: true });
    const { totpSessionChallenge } = yield select((state) => (isTrial ? state.trial : state.login));
    const body = {
      Code: (totpCode || '').trim(),
      ChallengeSession: totpSessionChallenge,
      TrustDevice: rememberDevice,
    };
    yield call(Api.fetch, '/account/verify_totp', {
      method: 'POST',
      body: JSON.stringify(body),
      onUnauthorized: 'throw',
      on5xx: 'throw',
    });
    yield put({ type: getActionType('LOADING', isTrial), loading: false });
    yield call(redirectAfterSuccessfulLogin, isTrial);
  } catch (error) {
    yield put({ type: getActionType('LOADING', isTrial), loading: false });

    const errorAction = getActionType('SET_ERROR', isTrial);
    const errMessage = getTOTPErrorMessage(error);

    yield put({
      type: errorAction,
      error: { ...error, message: errMessage },
    });
  }
}

function getTOTPErrorMessage(err) {
  if (err.error_code === 'invalid_challenge_session') {
    return 'Your verification session is invalid or has expired. Please reload the page and try again.';
  }
  if (err.error_code === 'invalid_code') {
    return 'The verification code is incorrect. Please try again.';
  }
  return err.message || 'Verification failed. Please try again.';
}

/* Validation rules */
const loginValidations = {
  // TODO (edimov) set proper email validation when ready to drop username login
  email: [
    {
      f: (v) => isLength(v),
      message: 'Please enter your username or email address.',
    },
    {
      f: validateCorporateMail,
      message: {
        message: errorMessage,
        link: {
          text: linkMessage,
          href: getCorporateLoginURL(),
        },
      },
    },
  ],
  password: [
    { f: isLength, message: 'Please enter your password.' },
    { f: (v) => isLength(v, { min: 6 }), message: 'Your password must be at least 6 characters long.' },
  ],
};

const warningRules = {
  password: [...registerWarnings.password],
};
